import { memo } from 'react';

import { ImagesIcon, TriangleAlertIcon } from '@air/design-icons';
import { HoverCard } from '@air/primitive-hover-card';
import { cn } from '@air/utils-tailwind';

import {
  WORKSPACE_AGENT_HOVER_CLOSE_DELAY_MS,
  WORKSPACE_AGENT_HOVER_OPEN_DELAY_MS,
} from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentHoverDelays';
import { workspaceAgentPanelPillInteraction } from '~/components/v2/components/workspace/workspace-agent-panel/constants/workspaceAgentPanelPillChrome';
import { WorkspaceAgentAssetListHoverPanel } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/WorkspaceAgentAssetListHoverPanel';
import { type WorkspaceAgentThumbnail } from '~/components/v2/components/workspace/workspace-agent-panel/design-system/cards/workspaceAgentThumbnailTypes';

/** Inline list-pill copy: wire `count`, not hydrated row length. */
const getAssetListOverflowLabel = (firstLabel: string, count: number) => {
  if (count <= 1) {
    return firstLabel;
  }

  const rest = count - 1;

  return `${firstLabel} and ${rest} ${rest === 1 ? 'other' : 'others'}`;
};

/** Prototype `GroupedImageInlineCard` leading block — 24px well, 12px Images glyph (`size-3`). */
const AssetListPillImagesLeading = memo(() => (
  <span className="flex size-6 shrink-0 items-center justify-center self-stretch overflow-hidden rounded-l-4 ads:bg-ads-gray-4 bg-gray-4 ads:text-icon-default text-gray-10">
    <ImagesIcon aria-hidden className="size-3" />
  </span>
));

AssetListPillImagesLeading.displayName = 'AssetListPillImagesLeading';

/** An unavailable member keeps its slot but has no name to lead with, so it is passed over. */
const getRepresentativeThumbnail = (thumbnails: WorkspaceAgentThumbnail[]) =>
  thumbnails.find((thumbnail) => !thumbnail.isUnavailable) ?? thumbnails[0];

const getListPillLabel = (thumbnails: WorkspaceAgentThumbnail[], count: number) => {
  const representative = getRepresentativeThumbnail(thumbnails);

  if (!representative) {
    return count === 1 ? '1 asset' : `${count} assets`;
  }

  return getAssetListOverflowLabel(representative.label, count);
};

export type WorkspaceAgentAssetListPillProps = {
  /** Declared list size from the wire. Overflow copy reads this, not `thumbnails.length`. */
  count: number;
  onOpenThumbnail?: (id: string) => void;
  thumbnails: WorkspaceAgentThumbnail[];
  /** Members the workspace can no longer return: the named count is kept and the shortfall marked. */
  unavailableCount: number;
};

/**
 * Compact summary pill for a multi-asset `assetList` message mention. Hovering opens a scrollable
 * asset list so the narrow panel can stay tidy until the user wants to browse the set.
 */
export const WorkspaceAgentAssetListPill = memo(
  ({ count, onOpenThumbnail, thumbnails, unavailableCount }: WorkspaceAgentAssetListPillProps) => {
    const representative = getRepresentativeThumbnail(thumbnails);
    const label = getListPillLabel(thumbnails, count);
    const accessibleLabel = unavailableCount > 0 ? `${label}, ${unavailableCount} unavailable` : label;

    if (!representative) {
      return (
        <span
          className="mx-0.5 inline-flex h-6 w-max min-w-0 max-w-64 items-center gap-1 overflow-hidden rounded-4 border ads:border-ads-gray-5 border-gray-5 ads:bg-subtle bg-gray-3 pr-1.5 font-medium ads:text-secondary text-12 text-gray-11"
          data-testid="WORKSPACE_AGENT_MESSAGE_ASSET_LIST_PILL"
        >
          <AssetListPillImagesLeading />
          <span className="min-w-0 truncate">{label}</span>
        </span>
      );
    }

    return (
      <span
        className="inline-flex max-w-full items-center align-middle leading-none"
        data-testid="WORKSPACE_AGENT_MESSAGE_ASSET_LIST_PILL"
      >
        <HoverCard
          className="w-auto min-w-0 overflow-hidden p-0"
          closeDelay={WORKSPACE_AGENT_HOVER_CLOSE_DELAY_MS}
          openDelay={WORKSPACE_AGENT_HOVER_OPEN_DELAY_MS}
          side="top"
          sideOffset={6}
          trigger={
            <button
              aria-label={accessibleLabel}
              className={cn(
                'inline-flex h-6 w-max min-w-0 max-w-64 items-center gap-1 overflow-hidden rounded-4 border ads:border-ads-gray-5 border-gray-5 ads:bg-subtle bg-gray-3 pr-1.5 font-medium ads:text-secondary text-12 text-gray-11',
                workspaceAgentPanelPillInteraction(),
                'mx-0.5 shrink-0 ads:hover:border-hover-input hover:border-gray-7 ads:hover:bg-hover-subtle hover:bg-gray-4',
              )}
              data-testid="WORKSPACE_AGENT_MESSAGE_ASSET_LIST_PILL_TRIGGER"
              type="button"
            >
              <AssetListPillImagesLeading />
              <span className="min-w-0 truncate whitespace-nowrap">{label}</span>
              {unavailableCount > 0 ? (
                <TriangleAlertIcon
                  aria-hidden
                  className="shrink-0 ads:text-icon-error text-red-11"
                  data-testid="WORKSPACE_AGENT_MESSAGE_ASSET_LIST_PILL_WARNING"
                  size="xs"
                />
              ) : null}
            </button>
          }
        >
          <WorkspaceAgentAssetListHoverPanel
            onOpenThumbnail={onOpenThumbnail}
            thumbnails={thumbnails}
            unavailableCount={unavailableCount}
          />
        </HoverCard>
      </span>
    );
  },
);

WorkspaceAgentAssetListPill.displayName = 'WorkspaceAgentAssetListPill';
